import React, { useEffect, useState } from 'react';
import { Box, Typography, Container, Grid, useTheme, Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper, Chip, TextField, MenuItem, Select, InputLabel, FormControl, ToggleButtonGroup, ToggleButton, Button, Alert, Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Menu } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import { VALID_MTOS } from '../utils/constants';
import { transactionStore, PartnerPermissionChange } from '../utils/transactionStore';
export interface PartnerTxnConfig {
  id: string;
  partnerName: string;
  b2cAllowed: boolean;
  c2bAllowed: boolean;
  b2bAllowed: boolean;
  lastUpdated: string;
}
type TxnType = 'B2B' | 'B2C' | 'C2B';
interface B2CLimit {
  id: string;
  type: string;
  purposeCode: string;
  currency: 'USD' | 'PKR';
  limit: number;
  effectiveDate: string;
}
interface UpcomingLimit {
  id: string;
  limitId: string;
  type: string;
  purposeCode: string;
  currency: 'USD' | 'PKR';
  newLimit: number;
  effectiveDate: string;
  status: 'Scheduled';
  isNewCategory?: boolean;
}
const MOCK_DATA: PartnerTxnConfig[] = VALID_MTOS.map((name, i) => ({
  id: `PTN-${1000 + i}`,
  partnerName: name,
  b2cAllowed: i % 2 === 0,
  c2bAllowed: i % 3 === 0,
  b2bAllowed: false,
  lastUpdated: `2026-0${7 - i % 6}-${10 + i}`
}));
const INITIAL_LIMITS: B2CLimit[] = [{
  id: 'lim-1',
  type: 'Freelance – Computer & Information Systems Services',
  purposeCode: 'Schedule J/O-3, Purpose Code 9186',
  currency: 'USD',
  limit: 25000,
  effectiveDate: '2026-01-01'
}, {
  id: 'lim-2',
  type: 'Other Freelance Services',
  purposeCode: 'Schedule J/O-3, Purpose Code 9249',
  currency: 'USD',
  limit: 25000,
  effectiveDate: '2026-01-01'
}, {
  id: 'lim-3',
  type: 'Pension Transactions',
  purposeCode: 'Schedule J/O-3, Purpose Code 9477',
  currency: 'PKR',
  limit: 250000,
  effectiveDate: '2026-01-01'
}];
interface Props {
  onLogout: () => void;
}
const FlagChip: React.FC<{
  value: boolean;
  warn?: boolean;
}> = ({
  value,
  warn = false
}) => <Chip label={value ? 'Y' : 'N'} size="small" sx={{
  bgcolor: value ? warn ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.12)' : 'rgba(107,114,128,0.12)',
  color: value ? warn ? '#D97706' : '#059669' : '#6B7280',
  fontWeight: 700,
  fontSize: '0.75rem',
  minWidth: 36
}} data-id="element-3921" data-mp="3fgca376f9jvb" />;
const fieldForType = (t: TxnType) => t === 'B2B' ? 'b2bAllowed' : t === 'B2C' ? 'b2cAllowed' : 'c2bAllowed';
const todayStr = () => new Date().toISOString().split('T')[0];
interface TabPanelProps {
  index: number;
  value: number;
  children: React.ReactNode;
}
const TabPanel: React.FC<TabPanelProps> = ({
  index,
  value,
  children
}) => <div hidden={value !== index} data-id="element-3922" data-mp="2iqcqmhoqh0xy">{value === index && <Box sx={{
    pt: 3
  }} data-id="element-3923" data-mp="2iro4xbft1r3b">{children}</Box>}</div>;
const SELECT_MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 320
    }
  }
};
const OTHER_CATEGORY_VALUE = '__OTHER__';
const DEACTIVATION_STATUS_CHIP: Record<string, {
  label: string;
  bg: string;
  color: string;
}> = {
  PENDING_CHECKER: {
    label: 'Pending Deactivation',
    bg: 'rgba(245,158,11,0.15)',
    color: '#D97706'
  },
  REJECTED: {
    label: 'Deactivation Rejected',
    bg: 'rgba(239,68,68,0.12)',
    color: '#EF4444'
  },
  APPROVED_SCHEDULED: {
    label: 'Deactivation Scheduled',
    bg: 'rgba(16,185,129,0.12)',
    color: '#059669'
  }
};
const RULE_STATUS_CHIP: Record<string, {
  label: string;
  bg: string;
  color: string;
}> = {
  PENDING_CHECKER: {
    label: 'Pending Checker',
    bg: 'rgba(245,158,11,0.15)',
    color: '#D97706'
  },
  REJECTED: {
    label: 'Rejected',
    bg: 'rgba(239,68,68,0.12)',
    color: '#EF4444'
  },
  APPROVED_SCHEDULED: {
    label: 'Approved · Scheduled',
    bg: 'rgba(16,185,129,0.12)',
    color: '#059669'
  }
};
const PartnerTxnConfigPage: React.FC<Props> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [partners, setPartners] = useState<PartnerTxnConfig[]>(MOCK_DATA);
  const [pendingRuleChanges, setPendingRuleChanges] = useState<PartnerPermissionChange[]>([]);
  const [limits, setLimits] = useState<B2CLimit[]>(INITIAL_LIMITS);
  const [upcomingLimits, setUpcomingLimits] = useState<UpcomingLimit[]>([]);

  // Re-render whenever the shared transaction store changes (checker approvals, etc.)
  const [, forceStoreSync] = useState(0);
  useEffect(() => {
    const sync = () => {
      const today = todayStr();

      // Once a checker approves a limit deactivation AND its effective date has arrived, the limit is applied (removed from the active table)
      const approvedLimitIds = transactionStore.getAllLimitDeactivations().filter(d => d.status === 'APPROVED' && d.effectiveDate <= today).map(d => d.limitId);
      if (approvedLimitIds.length) {
        setLimits(prev => prev.filter(l => !approvedLimitIds.includes(l.id)));
      }

      // Once a checker approves a partner permission change AND its effective date has arrived, apply it
      const allPermChanges = transactionStore.getAllPartnerPermissionChanges();
      const approvedEffective = allPermChanges.filter(c => c.status === 'APPROVED' && c.effectiveDate <= today);
      if (approvedEffective.length) {
        setPartners(prev => {
          let next = prev;
          approvedEffective.forEach(c => {
            next = next.map(p => p.id === c.partnerId ? {
              ...p,
              [fieldForType(c.txnType)]: c.newAllowed,
              lastUpdated: today
            } : p);
          });
          return next;
        });
      }
      // Surface everything still relevant: pending checker review, rejected, or approved-but-not-yet-effective
      setPendingRuleChanges(allPermChanges.filter(c => c.status === 'PENDING_CHECKER' || c.status === 'REJECTED' || c.status === 'APPROVED' && c.effectiveDate > today).sort((a, b) => (b.resubmittedAt || b.requestedAt).localeCompare(a.resubmittedAt || a.requestedAt)));
      forceStoreSync(v => v + 1);
    };
    sync();
    const unsubscribe = transactionStore.subscribe(sync);
    return unsubscribe;
  }, []);

  // --- Rule dialog state ---
  const [ruleDialogOpen, setRuleDialogOpen] = useState(false);
  const [formPartner, setFormPartner] = useState('');
  const [formType, setFormType] = useState<TxnType | ''>('');
  const [formAllowed, setFormAllowed] = useState<'Y' | 'N'>('Y');
  const [formDate, setFormDate] = useState('');
  const [formDateTouched, setFormDateTouched] = useState(false);

  // Preload current Y/N whenever partner + type are both selected
  useEffect(() => {
    if (formPartner && formType) {
      const p = partners.find(pp => pp.partnerName === formPartner);
      if (p) setFormAllowed(p[fieldForType(formType)] ? 'Y' : 'N');
    }
  }, [formPartner, formType, partners]);
  const dateIsPast = (d: string) => !!d && d < todayStr();
  const ruleDateError = formDateTouched && (!formDate ? 'Date of initiation is required' : dateIsPast(formDate) ? 'Date cannot be in the past' : '');
  const canApplyRule = !!formPartner && !!formType && !!formDate && !dateIsPast(formDate);
  const openRuleDialog = () => {
    setFormPartner('');
    setFormType('');
    setFormAllowed('Y');
    setFormDate('');
    setFormDateTouched(false);
    setRuleDialogOpen(true);
  };
  const handleApplyRule = () => {
    setFormDateTouched(true);
    if (!formPartner || !formType || !formDate || dateIsPast(formDate)) return;
    const p = partners.find(pp => pp.partnerName === formPartner);
    if (!p) return;
    const currentAllowed = p[fieldForType(formType)];
    const newAllowed = formAllowed === 'Y';
    transactionStore.submitPartnerPermissionChange({
      id: p.id,
      partnerName: p.partnerName,
      txnType: formType,
      currentAllowed,
      newAllowed
    }, `Txn type permission change requested for "${p.partnerName}" via Txn Type Config`, formDate);
    setRuleDialogOpen(false);
  };

  // --- Limit dialog state ---
  const [limitDialogOpen, setLimitDialogOpen] = useState(false);
  const [formLimitId, setFormLimitId] = useState('');
  const [formLimitValue, setFormLimitValue] = useState('');
  const [formLimitDate, setFormLimitDate] = useState('');
  const [limitTouched, setLimitTouched] = useState(false);

  // "Other" (new category) fields
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryPurposeCode, setNewCategoryPurposeCode] = useState('');
  const [newCategoryCurrency, setNewCategoryCurrency] = useState<'USD' | 'PKR'>('USD');
  const isOtherCategory = formLimitId === OTHER_CATEGORY_VALUE;
  const selectedLimit = !isOtherCategory ? limits.find(l => l.id === formLimitId) || null : null;

  // Preload current limit amount whenever a category is selected
  useEffect(() => {
    if (selectedLimit) setFormLimitValue(String(selectedLimit.limit));
    if (isOtherCategory) setFormLimitValue('');
  }, [formLimitId]);
  const limitValueError = limitTouched && (!formLimitValue ? 'Limit is required' : isNaN(Number(formLimitValue)) || Number(formLimitValue) <= 0 ? 'Enter a valid amount greater than 0' : '');
  const limitDateError = limitTouched && (!formLimitDate ? 'Effective date is required' : dateIsPast(formLimitDate) ? 'Date cannot be in the past' : '');
  const newCategoryNameError = limitTouched && isOtherCategory && !newCategoryName.trim() ? 'Category name is required' : '';
  const canApplyLimit = (isOtherCategory ? !!newCategoryName.trim() : !!formLimitId) && !!formLimitValue && !isNaN(Number(formLimitValue)) && Number(formLimitValue) > 0 && !!formLimitDate && !dateIsPast(formLimitDate);
  const openLimitDialog = () => {
    setFormLimitId('');
    setFormLimitValue('');
    setFormLimitDate('');
    setLimitTouched(false);
    setNewCategoryName('');
    setNewCategoryPurposeCode('');
    setNewCategoryCurrency('USD');
    setLimitDialogOpen(true);
  };
  const handleApplyLimit = () => {
    setLimitTouched(true);
    if (!canApplyLimit) return;
    const numeric = Number(formLimitValue);
    const today = todayStr();
    if (isOtherCategory) {
      // Brand new category — becomes a new row in the limits table
      const newId = `lim-${Date.now()}`;
      const type = newCategoryName.trim();
      const purposeCode = newCategoryPurposeCode.trim() || 'Pending Classification';
      if (formLimitDate === today) {
        setLimits(prev => [...prev, {
          id: newId,
          type,
          purposeCode,
          currency: newCategoryCurrency,
          limit: numeric,
          effectiveDate: today
        }]);
      } else {
        setUpcomingLimits(prev => [...prev, {
          id: `UL-${Date.now()}`,
          limitId: newId,
          type,
          purposeCode,
          currency: newCategoryCurrency,
          newLimit: numeric,
          effectiveDate: formLimitDate,
          status: 'Scheduled',
          isNewCategory: true
        }]);
      }
    } else if (selectedLimit) {
      if (formLimitDate === today) {
        setLimits(prev => prev.map(l => l.id === selectedLimit.id ? {
          ...l,
          limit: numeric,
          effectiveDate: today
        } : l));
      } else {
        setUpcomingLimits(prev => [...prev, {
          id: `UL-${Date.now()}`,
          limitId: selectedLimit.id,
          type: selectedLimit.type,
          purposeCode: selectedLimit.purposeCode,
          currency: selectedLimit.currency,
          newLimit: numeric,
          effectiveDate: formLimitDate,
          status: 'Scheduled'
        }]);
      }
    }
    setLimitDialogOpen(false);
  };

  // --- Deactivate limit flow (Actions column) ---
  const [limitMenuAnchorEl, setLimitMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [limitMenuTarget, setLimitMenuTarget] = useState<B2CLimit | null>(null);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<B2CLimit | null>(null);
  const [deactivateEffectiveDate, setDeactivateEffectiveDate] = useState('');
  const [deactivateDateTouched, setDeactivateDateTouched] = useState(false);
  const openLimitMenu = (e: React.MouseEvent<HTMLElement>, limit: B2CLimit) => {
    setLimitMenuAnchorEl(e.currentTarget);
    setLimitMenuTarget(limit);
  };
  const closeLimitMenu = () => {
    setLimitMenuAnchorEl(null);
    setLimitMenuTarget(null);
  };
  const openDeactivateConfirm = () => {
    if (!limitMenuTarget) return;
    setDeactivateTarget(limitMenuTarget);
    setDeactivateEffectiveDate('');
    setDeactivateDateTouched(false);
    setDeactivateConfirmOpen(true);
    closeLimitMenu();
  };
  const deactivateDateError = deactivateDateTouched && (!deactivateEffectiveDate ? 'Effective date is required' : dateIsPast(deactivateEffectiveDate) ? 'Date cannot be in the past' : '');
  const canDeactivate = !!deactivateEffectiveDate && !dateIsPast(deactivateEffectiveDate);
  const handleConfirmDeactivate = () => {
    setDeactivateDateTouched(true);
    if (!deactivateTarget || !canDeactivate) return;
    transactionStore.submitLimitDeactivation(deactivateTarget, `Deactivation requested for "${deactivateTarget.type}" via Txn Type Config`, deactivateEffectiveDate);
    setDeactivateConfirmOpen(false);
    setDeactivateTarget(null);
    setDeactivateEffectiveDate('');
    setDeactivateDateTouched(false);
  };
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const HEADERS = ['Partner', 'B2C', 'C2B', 'B2B', 'Last Updated'];
  return <Box sx={{
    display: 'flex',
    minHeight: '100vh',
    backgroundColor: isDarkMode ? '#0F0E24' : '#F8FAFC'
  }} data-id="element-3924" data-mp="q0evdp2ntqzs">
      <TopBar onMenuClick={() => setMobileOpen(!mobileOpen)} onLogout={onLogout} data-id="element-3925" data-mp="l592256xha1o" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="partner-txn-config" data-id="element-3926" data-mp="g6ekt7olztjo" />
      <Box component="main" sx={{
      flexGrow: 1,
      p: 3,
      width: {
        sm: `calc(100% - 240px)`
      },
      mt: 8
    }} data-id="element-3927" data-mp="1dpzj0l9rdcfe">
        <Container maxWidth="xl" data-id="element-3928" data-mp="18v3e4kjfaukv">
          <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 2
        }} data-id="element-3929" data-mp="sh5nfuw6n8ct">
            <Icon icon="lucide:sliders-horizontal" width={24} color="#5B4EF0" data-id="element-3930" data-mp="xgqms6f9liq2" />
            <Box data-id="element-3931" data-mp="xenm8sghk773">
              <Typography variant="h5" fontWeight={800} data-id="element-3932" data-mp="jgz2jny5n63m">Txn Type Configuration</Typography>
              <Typography variant="body2" color="text.secondary" data-id="element-3933" data-mp="jjbosday42mh">
                Manage which transaction types each partner is permitted to process
              </Typography>
            </Box>
          </Box>

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{
          mb: 1,
          borderBottom: `1px solid ${theme.palette.divider}`
        }} data-id="element-3934" data-mp="1g7883pxrpc1e">
            <Tab label="Partner Permissions" data-id="element-3935" data-mp="12dqqwbsk0bej" />
            <Tab label="B2C Limits" data-id="element-3936" data-mp="17e4qr6k04f1h" />
          </Tabs>

          {/* ================= TAB 1: PARTNER PERMISSIONS ================= */}
          <TabPanel value={tab} index={0} data-id="element-3937" data-mp="1v37gz2spbg7l">
            <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2
          }} data-id="element-3938" data-mp="1zuyzcq1pefmn">
              <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={16} data-id="element-3940" data-mp="298hkcofpoa3r" />} onClick={openRuleDialog} sx={{
              background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
              fontWeight: 700,
              borderRadius: 2
            }} data-id="element-3939" data-mp="1zx1zi2wklp53">
                Update Rule
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden',
            mb: 4
          }} data-id="element-3941" data-mp="2e1j1z47aynr1">
              <Table data-id="element-3942" data-mp="1mbo2wftn7jp6">
                <TableHead data-id="element-3943" data-mp="2092u9q3gi1js">
                  <TableRow sx={{
                  bgcolor: isDarkMode ? 'rgba(91,78,240,0.08)' : 'rgba(91,78,240,0.04)'
                }} data-id="element-3944" data-mp="20adxnoszp7ad">
                    {HEADERS.map(h => <TableCell key={h} sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    py: 1.5
                  }} data-id="element-3945" data-mp="1vcm7p0bwnv8t">
                        {h}
                      </TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-3946" data-mp="3irpnjgjkmfit">
                  {partners.map((p, i) => <TableRow key={p.id} sx={{
                  bgcolor: i % 2 === 0 ? 'transparent' : isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(91,78,240,0.06)' : 'rgba(91,78,240,0.04)'
                  },
                  transition: 'background-color 150ms'
                }} data-id="element-3947" data-mp="3ipwha2cvdxbc" data-mpi={i}>
                      <TableCell data-id="element-3948" data-mp="350ksysv28dfc">
                        <Typography fontWeight={600} fontSize="0.875rem" data-id="element-3949" data-mp="34zj67r4om2hs">{p.partnerName}</Typography>
                        <Typography variant="caption" color="text.secondary" data-id="element-3950" data-mp="39rt5vyvlmgx9">{p.id}</Typography>
                      </TableCell>
                      <TableCell data-id="element-3951" data-mp="2c9jc8nbpy9tp"><FlagChip value={p.b2cAllowed} data-id="element-3952" data-mp="2c9t127l12axt" /></TableCell>
                      <TableCell data-id="element-3953" data-mp="2h95nir7zlfzv"><FlagChip value={p.c2bAllowed} data-id="element-3954" data-mp="2h8vxx4qutaup" /></TableCell>
                      <TableCell data-id="element-3955" data-mp="29q65oq06bwim"><FlagChip value={p.b2bAllowed} warn data-id="element-3956" data-mp="29pwfb1b7vnce" /></TableCell>
                      <TableCell data-id="element-3957" data-mp="24rl6mtwnrivr"><Typography fontSize="0.8rem" color="text.secondary" data-id="element-3958" data-mp="24rux0ilm7s1z">{p.lastUpdated}</Typography></TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>

            <Typography fontWeight={800} fontSize="0.95rem" sx={{
            mb: 1.5
          }} data-id="element-3959" data-mp="1utorzs4ii5sw">Pending Rule Changes</Typography>
            {pendingRuleChanges.length === 0 ? <Paper elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: 3,
            textAlign: 'center'
          }} data-id="element-3960" data-mp="1m0lmc603yx8t">
                <Typography variant="body2" color="text.secondary" data-id="element-3961" data-mp="1h2ticdm6vko9">No pending or scheduled rule changes.</Typography>
              </Paper> : <TableContainer component={Paper} elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden'
          }} data-id="element-3962" data-mp="174m5o6yxnos2">
                <Table data-id="element-3963" data-mp="1354xh5s4gdj7">
                  <TableHead data-id="element-3964" data-mp="2qzqcgf159pz9">
                    <TableRow sx={{
                  bgcolor: isDarkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.05)'
                }} data-id="element-3965" data-mp="2r0i3rdnpl1jq">
                      {['Partner', 'Txn Type', 'Change', 'Effective Date', 'Status'].map(h => <TableCell key={h} sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    py: 1.5
                  }} data-id="element-3966" data-mp="2h0rb7hipkwma">
                          {h}
                        </TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-3967" data-mp="2d1s904od3dyj">
                    {pendingRuleChanges.map(r => {
                  const displayStatus = r.status === 'APPROVED' ? 'APPROVED_SCHEDULED' : r.status;
                  const chip = RULE_STATUS_CHIP[displayStatus] || RULE_STATUS_CHIP.PENDING_CHECKER;
                  return <TableRow key={r.id} data-id="element-3968" data-mp="1tboucbvwf7su">
                          <TableCell data-id="element-3969" data-mp="1taevhkh81vl5"><Typography fontWeight={600} fontSize="0.875rem" data-id="element-3970" data-mp="1tb6smzqgw1dj">{r.partnerName}</Typography></TableCell>
                          <TableCell data-id="element-3971" data-mp="1apifmowf4j43"><Chip label={r.txnType} size="small" sx={{
                        fontWeight: 700
                      }} data-id="element-3972" data-mp="1ap90xxgtpydp" /></TableCell>
                          <TableCell data-id="element-3973" data-mp="15qo0py1knep9"><Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75
                      }} data-id="element-3974" data-mp="1jo2u1duy46mi"><FlagChip value={r.currentAllowed} data-id="element-3975" data-mp="1jpnkose535f3" /><Icon icon="lucide:arrow-right" width={13} color="#9CA3AF" data-id="element-3976" data-mp="1eozo7r755wdh" /><FlagChip value={r.newAllowed} data-id="element-3977" data-mp="1eng7jzhkpjb6" /></Box></TableCell>
                          <TableCell data-id="element-3978" data-mp="1tl8kqkzndvm7"><Typography fontSize="0.8rem" data-id="element-3979" data-mp="1tkz2xkoraurl">{r.effectiveDate}</Typography></TableCell>
                          <TableCell data-id="element-3980" data-mp="1ome49pp5kj59">
                            <Chip label={chip.label} size="small" sx={{
                        bgcolor: chip.bg,
                        color: chip.color,
                        fontWeight: 700
                      }} data-id="element-3981" data-mp="1olcpba50nci9" />
                          </TableCell>
                        </TableRow>;
                })}
                  </TableBody>
                </Table>
              </TableContainer>}
          </TabPanel>

          {/* ================= TAB 2: B2C LIMITS ================= */}
          <TabPanel value={tab} index={1} data-id="element-3982" data-mp="2jvvivaora68l">
            <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2
          }} data-id="element-3983" data-mp="2juk7aop2ju6x">
              <Button variant="contained" startIcon={<Icon icon="lucide:plus" width={16} data-id="element-3985" data-mp="2tv4avy029asb" />} onClick={openLimitDialog} sx={{
              background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
              fontWeight: 700,
              borderRadius: 2
            }} data-id="element-3984" data-mp="2ewimnzbzvmtc">
                Update Limit
              </Button>
            </Box>

            <TableContainer component={Paper} elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden',
            mb: 4
          }} data-id="element-3986" data-mp="1w7lsrwkiewbn">
              <Table data-id="element-3987" data-mp="2axjmst54eg8r">
                <TableHead data-id="element-3988" data-mp="2659ytinkfq94">
                  <TableRow sx={{
                  bgcolor: isDarkMode ? 'rgba(91,78,240,0.08)' : 'rgba(91,78,240,0.04)'
                }} data-id="element-3989" data-mp="263yw7m5uwojl">
                    {['Category', 'Schedule / Purpose Code', 'Monthly Limit', 'Effective Date', 'Actions'].map(h => <TableCell key={h} sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    py: 1.5
                  }} data-id="element-3990" data-mp="1dg2s3wdyjucs">
                        {h}
                      </TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-3991" data-mp="3f3ffv5dy683z">
                  {limits.map(l => {
                  const deactRecord = transactionStore.getLatestLimitDeactivationForLimit(l.id);
                  const todayNow = todayStr();
                  const displayDeactStatus = deactRecord ? deactRecord.status === 'APPROVED' ? deactRecord.effectiveDate > todayNow ? 'APPROVED_SCHEDULED' : null : deactRecord.status === 'PENDING_CHECKER' || deactRecord.status === 'REJECTED' ? deactRecord.status : null : null;
                  const statusChip = displayDeactStatus ? DEACTIVATION_STATUS_CHIP[displayDeactStatus] : null;
                  return <TableRow key={l.id} data-id="element-3992" data-mp="xwxfqwefmruk">
                        <TableCell sx={{
                      maxWidth: 320
                    }} data-id="element-3993" data-mp="xwnsvhoop0t3">
                          <Typography fontWeight={600} fontSize="0.875rem" data-id="element-3994" data-mp="sxkmntwywojj">{l.type}</Typography>
                          {statusChip && <Chip label={displayDeactStatus === 'REJECTED' ? statusChip.label : `${statusChip.label} · ${deactRecord?.effectiveDate}`} size="small" sx={{
                        mt: 0.5,
                        bgcolor: statusChip.bg,
                        color: statusChip.color,
                        fontWeight: 700,
                        fontSize: '0.68rem'
                      }} data-id="element-3995" data-mp="o5t4o7rvnr50" />}
                        </TableCell>
                        <TableCell data-id="element-3996" data-mp="9otv0airgs1"><Typography fontSize="0.8rem" color="text.secondary" data-id="element-3997" data-mp="9ylstf4jy0d">{l.purposeCode}</Typography></TableCell>
                        <TableCell data-id="element-3998" data-mp="1zlhs1091ms6c">
                          <Typography fontWeight={700} fontSize="0.875rem" data-id="element-3999" data-mp="1zkgdumwqdpke">{l.currency} {l.limit.toLocaleString()}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{
                        display: 'block'
                      }} data-id="element-4000" data-mp="234qai9d9kn7g">per individual / month</Typography>
                        </TableCell>
                        <TableCell data-id="element-4001" data-mp="273z7dn3lp178"><Typography fontSize="0.8rem" color="text.secondary" data-id="element-4002" data-mp="2bws9e37opvfm">{l.effectiveDate}</Typography></TableCell>
                        <TableCell align="center" data-id="element-4003" data-mp="2gwemmcnijbof">
                          <IconButton size="small" onClick={e => openLimitMenu(e, l)} data-id="element-4004" data-mp="2gv3iucu2i3xb">
                            <Icon icon="lucide:more-vertical" width={18} data-id="element-4005" data-mp="1k77134mw92ki" />
                          </IconButton>
                        </TableCell>
                      </TableRow>;
                })}
                </TableBody>
              </Table>
            </TableContainer>

            <Menu anchorEl={limitMenuAnchorEl} open={Boolean(limitMenuAnchorEl)} onClose={closeLimitMenu} PaperProps={{
            sx: {
              borderRadius: 2
            }
          }} data-id="element-4006" data-mp="1y6ylpo7x1h7h">
              <MenuItem onClick={openDeactivateConfirm} disabled={(() => {
              if (!limitMenuTarget) return false;
              const rec = transactionStore.getLatestLimitDeactivationForLimit(limitMenuTarget.id);
              if (!rec) return false;
              if (rec.status === 'PENDING_CHECKER') return true;
              if (rec.status === 'APPROVED' && rec.effectiveDate > todayStr()) return true;
              return false;
            })()} sx={{
              color: '#EF4444',
              fontWeight: 600,
              gap: 1
            }} data-id="element-4007" data-mp="39mn7wddcic78">
                <Icon icon="lucide:shield-off" width={16} data-id="element-4008" data-mp="109rv2oy9rmuz" />
                Deactivate
              </MenuItem>
            </Menu>

            <Typography fontWeight={800} fontSize="0.95rem" sx={{
            mb: 1.5
          }} data-id="element-4009" data-mp="1a1xp04830ect">Upcoming Limit Changes</Typography>
            {upcomingLimits.length === 0 ? <Paper elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: 3,
            textAlign: 'center'
          }} data-id="element-4010" data-mp="1o1q8pfl6xuba">
                <Typography variant="body2" color="text.secondary" data-id="element-4011" data-mp="22of7tgbj65h5">No upcoming limit changes scheduled.</Typography>
              </Paper> : <TableContainer component={Paper} elevation={0} sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            overflow: 'hidden'
          }} data-id="element-4012" data-mp="27mqjzxpass2j">
                <Table data-id="element-4013" data-mp="1xojd67oq3qe9">
                  <TableHead data-id="element-4014" data-mp="1xnrhyxz1fuoi">
                    <TableRow sx={{
                  bgcolor: isDarkMode ? 'rgba(245,158,11,0.08)' : 'rgba(245,158,11,0.05)'
                }} data-id="element-4015" data-mp="1xr5mabr9idyl">
                      {['Category', 'New Limit', 'Effective Date', 'Status'].map(h => <TableCell key={h} sx={{
                    fontWeight: 700,
                    fontSize: '0.78rem',
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    py: 1.5
                  }} data-id="element-4016" data-mp="1oqh7heu1o9kn">
                          {h}
                        </TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody data-id="element-4017" data-mp="150dq3ea75pb9">
                    {upcomingLimits.map(u => <TableRow key={u.id} data-id="element-4018" data-mp="152hxi67b16ga">
                        <TableCell sx={{
                    maxWidth: 320
                  }} data-id="element-4019" data-mp="37nc2ij97brqh">
                          <Typography fontWeight={600} fontSize="0.875rem" data-id="element-4020" data-mp="37ovn2m1w4oy2">{u.type}</Typography>
                          {u.isNewCategory && <Chip label="New Category" size="small" sx={{
                      mt: 0.5,
                      bgcolor: 'rgba(91,78,240,0.12)',
                      color: '#5B4EF0',
                      fontWeight: 700,
                      fontSize: '0.68rem'
                    }} data-id="element-4021" data-mp="2xyapn1xydqgq" />}
                        </TableCell>
                        <TableCell data-id="element-4022" data-mp="3qju37q6131yv"><Typography fontWeight={700} fontSize="0.875rem" data-id="element-4023" data-mp="3qjkdm3owawtp">{u.currency} {u.newLimit.toLocaleString()}</Typography></TableCell>
                        <TableCell data-id="element-4024" data-mp="3gmz538do33l4"><Typography fontSize="0.8rem" data-id="element-4025" data-mp="3gmpg9o4cz2h0">{u.effectiveDate}</Typography></TableCell>
                        <TableCell data-id="element-4026" data-mp="3cl3v7fho7hvq"><Chip label="Scheduled" size="small" sx={{
                      bgcolor: 'rgba(245,158,11,0.15)',
                      color: '#D97706',
                      fontWeight: 700
                    }} data-id="element-4027" data-mp="3cldkt1yszn0w" /></TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </TableContainer>}
          </TabPanel>
        </Container>
      </Box>

      {/* ================= UPDATE RULE DIALOG ================= */}
      <Dialog open={ruleDialogOpen} onClose={() => setRuleDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-4028" data-mp="kqsvvbzqbkdf">
        <DialogTitle sx={{
        fontWeight: 800
      }} data-id="element-4029" data-mp="ugvzs78e2iqj">Update Transaction Type Permission</DialogTitle>
        <DialogContent sx={{
        pt: '12px !important',
        pb: 1
      }} data-id="element-4030" data-mp="zfpcvyjrk7q6">
          <Grid container spacing={3} sx={{
          mt: 0.5
        }} data-id="element-4031" data-mp="zf5s8a2qb58g">
            <Grid item xs={12} data-id="element-4032" data-mp="14dqwqlp0kb2p">
              <FormControl fullWidth size="small" sx={{
              minWidth: 260
            }} data-id="element-4033" data-mp="14cp97hqt9w43">
                <InputLabel id="rule-partner-label" shrink={!!formPartner || undefined} data-id="element-4034" data-mp="18h6szgx689ev">Partner</InputLabel>
                <Select labelId="rule-partner-label" id="rule-partner-select" label="Partner" value={formPartner} displayEmpty onChange={e => setFormPartner(e.target.value)} MenuProps={SELECT_MENU_PROPS} sx={{
                '& .MuiSelect-select': {
                  whiteSpace: 'normal'
                }
              }} data-id="element-4035" data-mp="1ddx9248z750a">
                  {VALID_MTOS.map(name => <MenuItem key={name} value={name} data-id="element-4036" data-mp="1uoj6sf42nbqm">{name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} data-id="element-4037" data-mp="1pqher4oo4230">
              <FormControl fullWidth size="small" sx={{
              minWidth: 260
            }} data-id="element-4038" data-mp="1prj2a8mveh1m">
                <InputLabel id="rule-type-label" shrink={!!formType || undefined} data-id="element-4039" data-mp="120n32qg34jkc">Transaction Type</InputLabel>
                <Select labelId="rule-type-label" id="rule-type-select" label="Transaction Type" value={formType} displayEmpty onChange={e => setFormType(e.target.value as TxnType)} MenuProps={SELECT_MENU_PROPS} data-id="element-4040" data-mp="xzuve1mv2mq6">
                  <MenuItem value="B2B" data-id="element-4041" data-mp="2ien09tnjpv3w">B2B</MenuItem>
                  <MenuItem value="B2C" data-id="element-4042" data-mp="2ig7utj9v1e1r">B2C</MenuItem>
                  <MenuItem value="C2B" data-id="element-4043" data-mp="462uikqygn4b">C2B</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} data-id="element-4044" data-mp="970dgzjshl6v">
              <Typography variant="caption" color="text.secondary" sx={{
              display: 'block',
              mb: 0.75
            }} data-id="element-4045" data-mp="932se8dnijkc">
                Allowed {formPartner && formType && <em style={{
                marginLeft: 4
              }} data-id="element-4046" data-mp="3z9b4ehi45u">(currently {formAllowed === 'Y' ? 'Enabled' : 'Disabled'})</em>}
              </Typography>
              <ToggleButtonGroup value={formAllowed} exclusive onChange={(_, v) => v && setFormAllowed(v)} fullWidth data-id="element-4047" data-mp="nv4xc6ukw2r8">
                <ToggleButton value="Y" sx={{
                fontWeight: 700
              }} data-id="element-4048" data-mp="rxrjps8hcvh1">Yes</ToggleButton>
                <ToggleButton value="N" sx={{
                fontWeight: 700
              }} data-id="element-4049" data-mp="e4a0y9nmbms2">No</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} data-id="element-4050" data-mp="16th8fy51vmpg">
              <TextField label="Date of Initiation" type="date" size="small" fullWidth value={formDate} onChange={e => {
              setFormDate(e.target.value);
              setFormDateTouched(true);
            }} onBlur={() => setFormDateTouched(true)} InputLabelProps={{
              shrink: true
            }} inputProps={{
              min: todayStr()
            }} error={!!ruleDateError} helperText={ruleDateError || ' '} data-id="element-4051" data-mp="16s649x7p0cxt" />
            </Grid>
            <Grid item xs={12} data-id="element-4052" data-mp="1ul2gkg1iva40">
              <Alert severity="info" icon={<Icon icon="lucide:clock" width={16} data-id="element-4054" data-mp="1umnb45nu6t1v" />} sx={{
              borderRadius: 2,
              fontSize: '0.8rem'
            }} data-id="element-4053" data-mp="1uluevymi7lxz">
                Note: This request will be sent to the checker for approval. If approved, the rule will become applicable from 12:00 AM (midnight) on the selected date of initiation.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-4055" data-mp="3f1fjw8rn6lkv">
          <Button onClick={() => setRuleDialogOpen(false)} sx={{
          borderRadius: 2
        }} data-id="element-4056" data-mp="3f30chsuebug3">Cancel</Button>
          <Button variant="contained" onClick={handleApplyRule} disabled={!canApplyRule} sx={{
          background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
          fontWeight: 700,
          borderRadius: 2
        }} data-id="element-4057" data-mp="3k23ibfi7a4p4">
            Send for Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= UPDATE LIMIT DIALOG ================= */}
      <Dialog open={limitDialogOpen} onClose={() => setLimitDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-4058" data-mp="3gapwoax8jypu">
        <DialogTitle sx={{
        fontWeight: 800
      }} data-id="element-4059" data-mp="36dbheen1tfz4">Update B2C Monthly Limit</DialogTitle>
        <DialogContent sx={{
        pt: '12px !important',
        pb: 1
      }} data-id="element-4060" data-mp="31ei52pjhzv0j">
          <Grid container spacing={3} sx={{
          mt: 0.5
        }} data-id="element-4061" data-mp="31h38vk3cwl0x">
            <Grid item xs={12} data-id="element-4062" data-mp="2xezvgpx4n6d0">
              <FormControl fullWidth size="small" sx={{
              minWidth: 260
            }} data-id="element-4063" data-mp="2xfrjnfoea1t9">
                <InputLabel id="limit-category-label" shrink={!!formLimitId || undefined} data-id="element-4064" data-mp="zndfdz14l4iu">Category</InputLabel>
                <Select labelId="limit-category-label" id="limit-category-select" label="Category" value={formLimitId} displayEmpty onChange={e => setFormLimitId(e.target.value)} MenuProps={SELECT_MENU_PROPS} renderValue={(val: string) => {
                if (val === OTHER_CATEGORY_VALUE) return 'Other (Add New Category)';
                const l = limits.find(x => x.id === val);
                return l ? l.type : '';
              }} sx={{
                '& .MuiSelect-select': {
                  whiteSpace: 'normal'
                }
              }} data-id="element-4065" data-mp="vn388j1lk82e">
                  {limits.map(l => <MenuItem key={l.id} value={l.id} data-id="element-4066" data-mp="2ywy74d24mc03">{l.type}</MenuItem>)}
                  <MenuItem value={OTHER_CATEGORY_VALUE} sx={{
                  fontStyle: 'italic',
                  color: '#5B4EF0',
                  fontWeight: 700,
                  gap: 1
                }} data-id="element-4067" data-mp="33sm0z5rjztbx">
                    <Icon icon="lucide:plus-circle" width={16} style={{
                    marginRight: 6
                  }} data-id="element-4068" data-mp="1ni1hb0y4biro" />
                    Other (Add New Category)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {selectedLimit && <Grid item xs={12} data-id="element-4069" data-mp="1fvmifrd2mg6d">
                <Typography variant="caption" color="text.secondary" data-id="element-4070" data-mp="1fyidn7xjaqr4">
                  {selectedLimit.purposeCode} · Current limit: <strong data-id="element-4071" data-mp="1ox3wl46x47sf">{selectedLimit.currency} {selectedLimit.limit.toLocaleString()}</strong> / individual / month
                </Typography>
              </Grid>}

            {isOtherCategory && <>
                <Grid item xs={12} data-id="element-4072" data-mp="w9hs7tpv8z4i">
                  <TextField label="New Category Name" size="small" fullWidth value={newCategoryName} onChange={e => {
                setNewCategoryName(e.target.value);
                setLimitTouched(true);
              }} onBlur={() => setLimitTouched(true)} error={!!newCategoryNameError} helperText={newCategoryNameError || ' '} placeholder="e.g. Home-based Business Services" data-id="element-4073" data-mp="w63skt8l740r" />
                </Grid>
                <Grid item xs={8} data-id="element-4074" data-mp="3hvu7vf57f4t8">
                  <TextField label="Schedule / Purpose Code (optional)" size="small" fullWidth value={newCategoryPurposeCode} onChange={e => setNewCategoryPurposeCode(e.target.value)} placeholder="e.g. Schedule J/O-3, Purpose Code XXXX" data-id="element-4075" data-mp="3hsonuebfmt4d" />
                </Grid>
                <Grid item xs={4} data-id="element-4076" data-mp="4hzbeo8qua4x">
                  <FormControl fullWidth size="small" data-id="element-4077" data-mp="9eg0plroiujl">
                    <InputLabel id="limit-new-currency-label" data-id="element-4078" data-mp="9cv4lrppv3jm">Currency</InputLabel>
                    <Select labelId="limit-new-currency-label" label="Currency" value={newCategoryCurrency} onChange={e => setNewCategoryCurrency(e.target.value as 'USD' | 'PKR')} data-id="element-4079" data-mp="dg19aux6kmnz">
                      <MenuItem value="USD" data-id="element-4080" data-mp="27p6oukpmysou">USD</MenuItem>
                      <MenuItem value="PKR" data-id="element-4081" data-mp="22xydvk8nqz9k">PKR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>}

            <Grid item xs={12} data-id="element-4082" data-mp="1xwiv5ytyd4uc">
              <TextField label={`New Monthly Limit${selectedLimit ? ` (${selectedLimit.currency})` : isOtherCategory ? ` (${newCategoryCurrency})` : ''}`} type="number" size="small" fullWidth value={formLimitValue} onChange={e => {
              setFormLimitValue(e.target.value);
              setLimitTouched(true);
            }} onBlur={() => setLimitTouched(true)} error={!!limitValueError} helperText={limitValueError || ' '} disabled={!formLimitId} data-id="element-4083" data-mp="1tx9xiivskmti" />
            </Grid>
            <Grid item xs={12} data-id="element-4084" data-mp="117t9dxf7fs33">
              <TextField label="Effective Date" type="date" size="small" fullWidth value={formLimitDate} onChange={e => {
              setFormLimitDate(e.target.value);
              setLimitTouched(true);
            }} onBlur={() => setLimitTouched(true)} InputLabelProps={{
              shrink: true
            }} inputProps={{
              min: todayStr()
            }} error={!!limitDateError} helperText={limitDateError || ' '} disabled={!formLimitId} data-id="element-4085" data-mp="116roz2catt8p" />
            </Grid>
            <Grid item xs={12} data-id="element-4086" data-mp="orbi84pgprdb">
              <Alert severity="info" icon={<Icon icon="lucide:clock" width={16} data-id="element-4088" data-mp="a1cms9paku1j" />} sx={{
              borderRadius: 2,
              fontSize: '0.8rem'
            }} data-id="element-4087" data-mp="orjxg39258ro">
                Note: The updated limit will become applicable from 12:00 AM (midnight) on the selected effective date.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-4089" data-mp="5zr0xyus55f7">
          <Button onClick={() => setLimitDialogOpen(false)} sx={{
          borderRadius: 2
        }} data-id="element-4090" data-mp="61bwnrstyuen">Cancel</Button>
          <Button variant="contained" onClick={handleApplyLimit} disabled={!canApplyLimit} sx={{
          background: 'linear-gradient(135deg, #5B4EF0 0%, #00B4B3 100%)',
          fontWeight: 700,
          borderRadius: 2
        }} data-id="element-4091" data-mp="3ikxupc4dqzuu">
            Apply Limit
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= DEACTIVATE LIMIT CONFIRM DIALOG ================= */}
      <Dialog open={deactivateConfirmOpen} onClose={() => setDeactivateConfirmOpen(false)} maxWidth="xs" fullWidth PaperProps={{
      sx: {
        borderRadius: 3
      }
    }} data-id="element-4092" data-mp="d8ddegkvk5v">
        <DialogTitle sx={{
        fontWeight: 800,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-4093" data-mp="f1lesqyfpv7r">
          <Icon icon="lucide:alert-triangle" width={22} color="#EF4444" data-id="element-4094" data-mp="a3juy3t6prv8" />
          Deactivate Limit?
        </DialogTitle>
        <DialogContent data-id="element-4095" data-mp="o0qcjv269kiv">
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 2.5
        }} data-id="element-4096" data-mp="j3zw36mggmwx">
            Do you want to deactivate the monthly limit for <strong data-id="element-4097" data-mp="1qgyt4akcqelw">{deactivateTarget?.type}</strong>? This request will be sent to the checker for approval — if approved, the limit is deactivated effective the date below; if rejected, it will appear in the Maker Inbox where it can be resent to the checker or deleted.
          </Typography>
          <TextField label="Effective Date" type="date" size="small" fullWidth value={deactivateEffectiveDate} onChange={e => {
          setDeactivateEffectiveDate(e.target.value);
          setDeactivateDateTouched(true);
        }} onBlur={() => setDeactivateDateTouched(true)} InputLabelProps={{
          shrink: true
        }} inputProps={{
          min: todayStr()
        }} error={!!deactivateDateError} helperText={deactivateDateError || ' '} sx={{
          mb: 1.5
        }} data-id="element-4098" data-mp="24g6sb3gilmq3" />
          <Alert severity="info" icon={<Icon icon="lucide:clock" width={16} data-id="element-4100" data-mp="t09pcbbud29v" />} sx={{
          borderRadius: 2,
          fontSize: '0.8rem'
        }} data-id="element-4099" data-mp="nzudjo77k2u0">
            Note: Once approved by the checker, the deactivation will take effect from 12:00 AM (midnight) on the selected date.
          </Alert>
        </DialogContent>
        <DialogActions sx={{
        px: 3,
        pb: 2.5,
        gap: 1
      }} data-id="element-4101" data-mp="ggcuebxvlexz">
          <Button onClick={() => setDeactivateConfirmOpen(false)} sx={{
          borderRadius: 2
        }} data-id="element-4102" data-mp="bhrqo2jf0d4s">Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDeactivate} disabled={!canDeactivate} sx={{
          borderRadius: 2,
          fontWeight: 700
        }} data-id="element-4103" data-mp="7gpslp18i74e">
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default PartnerTxnConfigPage;